import React, { useCallback, useEffect, useState } from 'react';
import {
  AppButton,
  Box,
  CardTypesEnum,
  DividerIcon,
  Inscription,
  NewPlansIcon,
  Preloader,
  Text,
  useDebounce,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import { validatePlans } from '@/modules/Renewals/RenewalSetup/PlanYearPlansStep/PlansSummaryContainer/hooks/usePlanCodeActions.tsx';
import theme from '@/styles/theme';

import { Renewal } from '../../renewals.types';
import { useRunActiveCodeValidation } from '../hooks';
// import { useHandleExpressSetupSetUp } from '../../hooks';
import { RENEWAL_SETUP_CONTENT_WIDTH } from '../RenewalSetup';
import { RenewalPlanOptionValues, RenewalSetupStep } from '../RenewalSetup.types';
import RenewalSetupLinks from '../RenewalSetupLinks';
import { usePlansStore, useRenewalSetupStore } from '../stores';
import usePayrollCalendarsStore from '../stores/usePayrollCalendars.store';
import { StepOption } from '../stores/useRenewalSetup.store';
import { getStepLabel, hasAnyFieldError } from '../utils';

import { useRenewalPayrollCalendarEdit } from './PayrollCalendarSection/hooks/useRenewalPayrollCalendarEdit';
import RenewalPayrollCalendarModal from './PayrollCalendarSection/RenewalPayrollCalendarModal';
import PayrollCalendarSection from './PayrollCalendarSection';
import PlansSummary from './PlansSummary';
import { PlansSummaryContainer } from './PlansSummaryContainer';
import { PlanYearsPlansForm } from './PlanYearsPlansForm';
import RenewalDetailsNote from './RenewalDetailsNote';
import RenewalFAQ from './RenewalFAQ';

interface Props {
  steps: RenewalSetupStep[];
  currentStep: RenewalSetupStep;
  renewalSetupData?: Renewal;
  handleStepName: (stepName: RenewalSetupStep) => void;
  handleSetVisibleRenewPopup: (value: boolean) => void;
}

const PlanYearPlansStep: React.FC<Props> = ({
  steps,
  currentStep,
  renewalSetupData,
  handleStepName,
  handleSetVisibleRenewPopup,
}) => {
  const [showNoteError, setShowNoteError] = useState(false);
  const isRenewalCalendarEnabled = useFeatureFlag(FeatureFlags.RENEWAL_CALENDAR);
  const plansRenewSelectedOption = useRenewalSetupStore((state) => state.plansPlanYear.selectedOption);
  const planDetailsNote = useRenewalSetupStore((state) => state.planDetailsNote);
  const isOptionChosen = useRenewalSetupStore((state) => state.plansPlanYear.isOptionChosen);
  const setStepState = useRenewalSetupStore((state) => state.setStepState);
  const plans = usePlansStore((state) => state.state.plans);
  const errors = usePlansStore((state) => state.state.errors);
  const setState = usePlansStore((state) => state.setState);
  const setIsRevalidationPending = useRenewalSetupStore((state) => state.setIsRevalidationPending);
  const serverInvalidCodes = useRenewalSetupStore(
    (state) => state.serverInvalidCodes[RenewalSetupStep.PlansPlanYear]
  );

  const { handleEditCalendar, handleCalendarReady } = useRenewalPayrollCalendarEdit();
  const payrollCalendars = usePayrollCalendarsStore((state) => state.state.payrollCalendars);

  const {
    runActiveValidation,
    isValidating: isLoading,
    showPlansError,
    setShowPlansError,
  } = useRunActiveCodeValidation({
    step: RenewalSetupStep.PlansPlanYear,
    orgId: renewalSetupData?.orgId,
    getHasClientErrors: () => hasAnyFieldError(errors),
    getPlanCodes: () => plans?.map((p) => p.planCode) || [],
  });

  const plansCodesKey = (plans || []).map((p) => p.planCode).join('|');
  const debouncedCodesKey = useDebounce(plansCodesKey);

  useEffect(() => {
    if (!isOptionChosen || plansRenewSelectedOption !== RenewalPlanOptionValues.ACTIVE) return;
    setIsRevalidationPending(true);
  }, [plansCodesKey, isOptionChosen, plansRenewSelectedOption, setIsRevalidationPending]);

  useEffect(() => {
    if (!isOptionChosen || plansRenewSelectedOption !== RenewalPlanOptionValues.ACTIVE) return;
    runActiveValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCodesKey, isOptionChosen, plansRenewSelectedOption]);

  const handleChooseOption = useCallback(() => {
    setStepState(RenewalSetupStep.PlansPlanYear, { isOptionChosen: true });
    if (plansRenewSelectedOption === RenewalPlanOptionValues.ACTIVE) {
      runActiveValidation();
    }
  }, [plansRenewSelectedOption, runActiveValidation, setStepState]);

  const handleOptionChange = useCallback(
    (option: StepOption) => {
      setStepState(RenewalSetupStep.PlansPlanYear, { selectedOption: option });
      if (!isOptionChosen) return;

      if (option === RenewalPlanOptionValues.ACTIVE) {
        runActiveValidation();
      } else {
        setShowPlansError(false);
        setStepState(RenewalSetupStep.PlansPlanYear, { isFailed: false });
      }
    },
    [isOptionChosen, runActiveValidation, setShowPlansError, setStepState]
  );

  const handleSubmit = useCallback(async () => {
    const selected = plansRenewSelectedOption;
    const nextStep = steps[steps.indexOf(currentStep) + 1];

    const noteRequired =
      selected === RenewalPlanOptionValues.REQUESTED || selected === RenewalPlanOptionValues.CANCELED;

    if (noteRequired && !planDetailsNote) {
      setShowNoteError(true);
      return;
    }

    // Popup flow
    if (selected === RenewalPlanOptionValues.CANCELED) {
      handleSetVisibleRenewPopup(true);
      return;
    }

    // Non-active moves forward without validation
    if (selected !== RenewalPlanOptionValues.ACTIVE) {
      setStepState(RenewalSetupStep.PlansPlanYear, { isSubmitted: true });
      handleStepName(nextStep);
      return;
    }

    const isValid = await runActiveValidation();
    if (!isValid) {
      return;
    }

    setStepState(RenewalSetupStep.PlansPlanYear, { isSubmitted: true });
    handleStepName(nextStep);
  }, [
    currentStep,
    handleSetVisibleRenewPopup,
    handleStepName,
    planDetailsNote,
    plansRenewSelectedOption,
    runActiveValidation,
    setStepState,
    steps,
  ]);

  useEffect(() => {
    if (!plans?.length) return;
    setState((prev) => ({ ...prev, errors: validatePlans(plans, serverInvalidCodes || []) }));
  }, [plans, serverInvalidCodes, setState]);

  return (
    <>
      <Box style={{ maxWidth: RENEWAL_SETUP_CONTENT_WIDTH }} data-testid="plan-year-plans-step">
        <Box margin={{ bottom: 'xs' }}>
          <NewPlansIcon size="48px" color={theme.colors.cardTypes[CardTypesEnum.HSA]} />
        </Box>
        <Inscription weight="bold" size="32px" lineHeight="44px" color="textTitle" margin={{ bottom: 'xs' }}>
          Renew Your Plan for the Upcoming Plan Year
        </Inscription>
        <Text color="textBody" margin={{ bottom: 'l' }}>
          Your current plan year ends on{' '}
          <Text as="span" weight={700}>
            {dayjs(renewalSetupData?.planYear?.validTo).format(DEFAULT_DATE_FORMAT)}
          </Text>
          . Renewing plans will be from{' '}
          <Text as="span" weight={700}>
            {dayjs(renewalSetupData?.newPlanYear?.validFrom).format(DEFAULT_DATE_FORMAT)}
          </Text>{' '}
          to{' '}
          <Text as="span" weight={700}>
            {dayjs(renewalSetupData?.newPlanYear?.validTo).format(DEFAULT_DATE_FORMAT)}
          </Text>
          .
        </Text>
        <RenewalFAQ renewalSetupData={renewalSetupData} />
        <PlansSummary renewalSetupData={renewalSetupData} />
        <PlanYearsPlansForm
          plansRenewSelectedOption={plansRenewSelectedOption}
          onOptionChange={handleOptionChange}
        />
        {isOptionChosen ? null : (
          <AppButton
            disabled={!plansRenewSelectedOption}
            onClick={handleChooseOption}
            testId="choose-option"
            style={{ marginTop: theme.spacings.l, height: '54px' }}
            containerStyle={{ maxWidth: 'none', width: '100%' }}
          >
            Choose Option
          </AppButton>
        )}
        {isOptionChosen && (
          <>
            <Box margin={{ vertical: 'l' }}>
              <DividerIcon width="100%" />
            </Box>
            {plansRenewSelectedOption === RenewalPlanOptionValues.ACTIVE && (
              <>
                <Inscription
                  weight="bold"
                  size="32px"
                  lineHeight="44px"
                  color="textTitle"
                  margin={{ bottom: 'l' }}
                >
                  Summary of Your Plans
                </Inscription>
                <Box background="module" pad="l" round="container1Round">
                  <PlansSummaryContainer
                    planYear={renewalSetupData?.newPlanYear}
                    plans={renewalSetupData?.plans}
                    showPlansError={showPlansError}
                    isEditMode
                  />
                </Box>
                {isRenewalCalendarEnabled && (payrollCalendars?.length ?? 0) > 0 && (
                  <PayrollCalendarSection
                    payrollCalendars={payrollCalendars}
                    onEditCalendar={handleEditCalendar}
                  />
                )}
                {isRenewalCalendarEnabled && (
                  <RenewalPayrollCalendarModal onCalendarReady={handleCalendarReady} />
                )}
              </>
            )}
            {plansRenewSelectedOption === RenewalPlanOptionValues.REQUESTED && (
              <>
                <Inscription
                  weight="bold"
                  size="32px"
                  lineHeight="44px"
                  color="textTitle"
                  margin={{ bottom: 'xs' }}
                >
                  Renew Your Plans
                </Inscription>
                <Inscription lineHeight="20px" color="textBody" margin={{ bottom: 'l' }}>
                  Your plan renewal options are managed by{' '}
                  <Inscription weight="bold">{renewalSetupData?.partnerName}</Inscription>. Please specify any
                  changes you would like to make in your upcoming plan and we will be in touch with you
                  shortly.
                </Inscription>
                <Box background="module" pad="l" round="container1Round">
                  <RenewalDetailsNote
                    title="Renewal details"
                    placeholder="Specify what changes you would like to make to your plans"
                    showError={showNoteError}
                    handleSetShowError={setShowNoteError}
                  />
                </Box>
              </>
            )}
            {plansRenewSelectedOption === RenewalPlanOptionValues.CANCELED && (
              <>
                <Inscription
                  weight="bold"
                  size="32px"
                  lineHeight="44px"
                  color="textTitle"
                  margin={{ bottom: 'l' }}
                >
                  Do not set up any plans for the upcoming plan year?
                </Inscription>
                <Box background="module" pad="l" round="container1Round">
                  <RenewalDetailsNote
                    title="Reason for not renewing"
                    placeholder="Specify your reason for not renewing"
                    showError={showNoteError}
                    handleSetShowError={setShowNoteError}
                  />
                </Box>
              </>
            )}
            {(plansRenewSelectedOption === RenewalPlanOptionValues.ACTIVE ||
              plansRenewSelectedOption === RenewalPlanOptionValues.REQUESTED) && (
              <Inscription color="textBody" weight="bold" textAlign="center" margin={{ top: 'l' }}>
                Look good? If so, proceed to the next step!
              </Inscription>
            )}
            {(plansRenewSelectedOption === RenewalPlanOptionValues.ACTIVE ||
              plansRenewSelectedOption === RenewalPlanOptionValues.REQUESTED) && (
              <AppButton
                onClick={handleSubmit}
                disabled={isLoading}
                testId="submit-plans-renew"
                style={{ height: '54px', marginTop: theme.spacings.s }}
                containerStyle={{ maxWidth: 'none', width: '100%' }}
              >
                {isLoading ? <Preloader /> : getStepLabel(steps, currentStep)}
              </AppButton>
            )}
            {plansRenewSelectedOption === RenewalPlanOptionValues.CANCELED && (
              <AppButton
                onClick={handleSubmit}
                testId="do-not-renew"
                style={{ height: '54px', marginTop: theme.spacings.l }}
                containerStyle={{ maxWidth: 'none', width: '100%' }}
                color="red"
              >
                Do Not Renew (1/2)
              </AppButton>
            )}
          </>
        )}

        <Box margin={{ top: 'xl', bottom: 'l' }}>
          <DividerIcon width="100%" />
        </Box>

        <RenewalSetupLinks renewalSetupData={renewalSetupData} />
      </Box>
    </>
  );
};

export default PlanYearPlansStep;
